// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/lib/gapCarousel.ts — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
/**
 * Pure helpers behind shared/ui/journey/GapCarousel.tsx — the one-question-
 * at-a-time card for facts we still need. Kept out of the component so the
 * counting and labelling rules are testable without a DOM (physician-ui runs
 * vitest in the node environment).
 * Canonical: shared/lib/gapCarousel.ts.
 */

export type GapQuestion = {
  key: string;
  stem: string;
  help?: string;
  type: "text" | "choice" | "date" | "tel" | "email" | "number";
  options?: string[];
  placeholder?: string;
  optional?: boolean;
};

export type GapAnswers = Record<string, string>;

function isAnswered(answers: GapAnswers, key: string): boolean {
  const v = answers[key];
  // Whitespace alone is not an answer: the Next/Skip label must not flip to
  // "Next" because someone pressed the space bar.
  return typeof v === "string" && v.trim().length > 0;
}

export function answeredCount(questions: GapQuestion[], answers: GapAnswers): number {
  // Count only keys that belong to this question set, so answers carried in
  // from another step don't inflate "K answered".
  return questions.filter((q) => isAnswered(answers, q.key)).length;
}

export function progressPercent(index: number, total: number): number {
  if (!(total > 0)) return 0;
  const pct = Math.round((index / total) * 100);
  return Math.min(100, Math.max(0, pct));
}

export function nextLabel(
  question: GapQuestion | undefined,
  answers: GapAnswers,
  isLast: boolean,
): "Next" | "Skip" | "Continue" {
  if (isLast) return "Continue";
  if (question && isAnswered(answers, question.key)) return "Next";
  return "Skip";
}

export function dotStates(
  questions: GapQuestion[],
  index: number,
  answers: GapAnswers,
): Array<{ width: number; color: "current" | "answered" | "empty" }> {
  return questions.map((q, i) => {
    if (i === index) return { width: 22, color: "current" as const };
    return {
      width: 8,
      color: isAnswered(answers, q.key) ? ("answered" as const) : ("empty" as const),
    };
  });
}

export function clampIndex(i: number, total: number): number {
  if (!(total > 0) || !Number.isFinite(i)) return 0;
  return Math.min(total - 1, Math.max(0, Math.floor(i)));
}

// ── Grouped steps (Michael, 2026-10-09) ──────────────────────────────────────
//
// "Bunch together address, first, last name, sex, date of birth": a carousel
// step can hold a whole topic, so the person fills several boxes on one card
// instead of paging through them one at a time.

export type GapGroup = { key: string; title: string; keys: string[] };

export type GapStep = { key: string; title: string; questions: GapQuestion[] };

export const OTHER_STEP_TITLE = "Other details";
const OTHER_STEP_KEY = "__other";

/**
 * The carousel's steps. Without groups every question is its own step (the
 * original one-per-card walk). With groups, each group becomes one step in the
 * order given, its questions in the group's key order; groups matching no
 * question are dropped, a key claimed twice goes to the first group, and
 * questions no group names trail in an "Other details" step so nothing asked
 * is silently left out.
 */
export function buildSteps(questions: GapQuestion[], groups?: GapGroup[] | null): GapStep[] {
  if (!groups) return questions.map((q) => ({ key: q.key, title: q.stem, questions: [q] }));
  const byKey = new Map(questions.map((q) => [q.key, q] as const));
  const placed = new Set<string>();
  const steps: GapStep[] = [];
  for (const g of groups) {
    const qs: GapQuestion[] = [];
    for (const k of g.keys) {
      const q = byKey.get(k);
      if (!q || placed.has(k)) continue;
      placed.add(k);
      qs.push(q);
    }
    if (qs.length) steps.push({ key: g.key, title: g.title, questions: qs });
  }
  const rest = questions.filter((q) => !placed.has(q.key));
  if (rest.length) {
    // A caller-supplied "Other details" group absorbs the leftovers rather
    // than producing two cards under the same heading.
    const existing = steps.find((s) => s.title === OTHER_STEP_TITLE);
    if (existing) existing.questions = [...existing.questions, ...rest];
    else steps.push({ key: OTHER_STEP_KEY, title: OTHER_STEP_TITLE, questions: rest });
  }
  return steps;
}

/**
 * The card's heading. A one-question step is headed by its stem, exactly as
 * the one-per-card walk always was; a group is headed by its title, with each
 * question's stem demoted inside the card.
 */
export function stepHeading(step: GapStep): { heading: string; grouped: boolean } {
  if (step.questions.length === 1) return { heading: step.questions[0].stem, grouped: false };
  return { heading: step.title, grouped: true };
}

// A card reads as done when everything it must have is answered; an
// all-optional card counts once any of it is, so a blank one stays "empty".
function stepAnswered(step: GapStep, answers: GapAnswers): boolean {
  const required = step.questions.filter((q) => !q.optional);
  if (required.length) return required.every((q) => isAnswered(answers, q.key));
  return answeredCount(step.questions, answers) > 0;
}

export function stepNextLabel(
  step: GapStep | undefined,
  answers: GapAnswers,
  isLast: boolean,
): "Next" | "Skip" | "Continue" {
  if (isLast) return "Continue";
  if (step && answeredCount(step.questions, answers) > 0) return "Next";
  return "Skip";
}

export function stepDotStates(
  steps: GapStep[],
  index: number,
  answers: GapAnswers,
): Array<{ width: number; color: "current" | "answered" | "empty" }> {
  return steps.map((s, i) => {
    if (i === index) return { width: 22, color: "current" as const };
    return { width: 8, color: stepAnswered(s, answers) ? ("answered" as const) : ("empty" as const) };
  });
}
